import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbJsonLd, faqJsonLd } from '../../components/SEO';
import { useDisplayCurrency } from '../../hooks/useDisplayCurrency';
import CurrencySelect from './CurrencySelect';
import {
  PLAN_ORDERS_PER_DRIVER,
  PLAN_PRICE_USD,
  getPlanPriceDisplay,
  isLitePlan,
  type PlanKey,
} from './pricingDisplay';
import { DEMO_URL, LITE_SIGNUP_URL, SIGNUP_URL } from '../../data/links';
import { formatMoney } from '../../lib/currency';

const MIN_DRIVERS = 10;

const PLATFORM_PLANS: PlanKey[] = ['professional', 'premium', 'enterprise'];
const PLAN_ICONS: Partial<Record<PlanKey, string>> = {
  professional: 'ri-route-line',
  premium: 'ri-sparkling-2-line',
  enterprise: 'ri-building-4-line',
};

const planHref = (key: PlanKey) =>
  key === 'enterprise' ? DEMO_URL : isLitePlan(key) ? LITE_SIGNUP_URL : SIGNUP_URL;

const perDriverOffer = (name: string, price: number, description: string) => ({
  '@type': 'Offer',
  name,
  price: String(price),
  priceCurrency: 'USD',
  description,
  priceSpecification: {
    '@type': 'UnitPriceSpecification',
    price: String(price),
    priceCurrency: 'USD',
    unitText: 'driver per month',
    referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'driver per month' },
  },
});

const PRICING_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Geofleet',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, iOS, Android',
  url: 'https://geofleet.ai/pricing',
  offers: [
    perDriverOffer('Geofleet Professional', 19, 'Minimum 10 drivers with 5,000 orders, integrations, route optimization and Autopilot.'),
    perDriverOffer('Geofleet Premium AI', 29, 'Minimum 10 drivers with 10,000 orders, branded experiences and AI dispatch.'),
    { '@type': 'Offer', name: 'Geofleet Enterprise', description: 'Custom pricing and contracted order volume for large logistics networks.' },
  ],
};

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const { currency, setCurrency, rateLine } = useDisplayCurrency();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = useMemo(() => {
    const raw = t('pricing.faqs', { returnObjects: true, lng: i18n.language });
    return Array.isArray(raw) ? (raw as { question: string; answer: string }[]) : [];
  }, [t, i18n.language]);

  /** Monthly price with the included drivers, so Command reads on the same basis as Lite. */
  const monthlyFor = (key: PlanKey) => {
    const price = PLAN_PRICE_USD[key];
    return price === null ? getPlanPriceDisplay(key, t, currency) : formatMoney(price * MIN_DRIVERS, currency);
  };

  const featuresFor = (key: PlanKey) => {
    const raw = t(`pricing.plans.${key}.features`, { returnObjects: true, lng: i18n.language });
    return Array.isArray(raw) ? (raw as string[]) : [];
  };

  const pricingHeroPlans = PLATFORM_PLANS;

  return (
    <div className="pt-[60px]">
      <SEO
        title="Geofleet Command Pricing"
        description="Compare Geofleet Command delivery plans. Professional starts at $19 per driver per month with a 10-driver minimum."
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://geofleet.ai' },
            { name: 'Pricing', url: 'https://geofleet.ai/pricing' },
            { name: 'Geofleet Command', url: 'https://geofleet.ai/pricing' },
          ]),
          PRICING_JSON_LD,
          faqJsonLd(faqs),
        ]}
      />

      <section className="relative isolate overflow-hidden border-b border-white/10 bg-[#071421] py-14 text-white lg:py-16">
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute -right-24 -top-48 h-[560px] w-[560px] rounded-full bg-[#1769ff]/15 blur-[100px]" />
          <div className="absolute -bottom-56 left-[28%] h-[420px] w-[650px] rounded-full bg-cyan-400/[0.07] blur-[100px]" />
          <div className="absolute inset-0 bg-[linear-gradient(115deg,transparent_35%,rgba(255,255,255,0.035)_35.1%,transparent_35.3%,transparent_65%,rgba(255,255,255,0.025)_65.1%,transparent_65.3%)]" />
        </div>
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.05fr_.95fr] lg:gap-8">
          <div className="text-center lg:text-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-blue-200">
              <i className="ri-price-tag-3-line" aria-hidden />
              {t('pricing.badge')}
            </span>
            <div className="mx-auto mt-7 inline-flex rounded-xl border border-white/10 bg-white/[0.06] p-1 lg:mx-0">
              <Link to="/pricing/lite" className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/10 hover:text-white">{t('pricing.lite.eyebrow')}</Link>
              <span className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-950" aria-current="page">{t('pricing.platform.eyebrow')}</span>
            </div>
            <h1 className="mx-auto mt-5 max-w-3xl text-4xl font-bold tracking-[-0.035em] sm:text-5xl lg:mx-0 lg:text-[3.5rem] lg:leading-[1.05]">{t('pricing.platform.title')}</h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300 lg:mx-0">{t('pricing.platform.subtitle')}</p>
            <div className="mt-7 flex flex-col items-center gap-3 lg:items-start">
              <CurrencySelect currency={currency} onChange={setCurrency} />
              {rateLine ? <p className="max-w-2xl text-sm text-slate-400">{t('pricing.currencyNote', { rate: rateLine })}</p> : null}
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[600px] py-4 lg:py-8" aria-hidden="true">
            <div className="absolute inset-x-[12%] top-[15%] h-[70%] rounded-full bg-[#1769ff]/20 blur-[70px]" />
            <div className="relative grid grid-cols-3 items-end gap-2 sm:gap-3">
              {pricingHeroPlans.map((key, index) => {
                const isFeatured = key === 'professional';
                return (
                  <div
                    key={key}
                    className={`relative overflow-hidden rounded-2xl border p-3 shadow-2xl backdrop-blur-md sm:rounded-3xl sm:p-5 ${
                      isFeatured
                        ? 'z-10 min-h-[220px] border-blue-300/60 bg-[#1769ff] text-white sm:min-h-[270px]'
                        : 'min-h-[190px] border-white/15 bg-[#102235]/90 text-white/90 sm:min-h-[230px]'
                    } ${index === 0 ? 'translate-y-5 sm:translate-y-7' : ''} ${index === 2 ? 'translate-y-5 sm:translate-y-7' : ''}`}
                  >
                    {isFeatured ? <div className="absolute -right-10 -top-12 h-32 w-32 rounded-full border border-white/15" /> : null}
                    <div className="relative">
                      <span className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm ${isFeatured ? 'bg-white/15' : 'bg-white/[0.08] text-blue-200'}`}>
                        <i className={key === 'lite' ? 'ri-flashlight-line' : key === 'professional' ? 'ri-route-line' : 'ri-sparkling-2-line'} />
                      </span>
                      <p className="mt-5 min-h-10 text-xs font-semibold leading-snug sm:text-sm">{t(`pricing.plans.${key}.name`)}</p>
                      <p className="mt-2 whitespace-nowrap text-lg font-bold tracking-tight sm:text-2xl">{monthlyFor(key)}</p>
                      <p className={`mt-1 text-[10px] sm:text-xs ${isFeatured ? 'text-blue-100' : 'text-slate-400'}`}>{PLAN_PRICE_USD[key] !== null ? t('pricing.perMonth') : ''}</p>
                      <div className={`mt-5 h-px ${isFeatured ? 'bg-white/20' : 'bg-white/10'}`} />
                      <div className="mt-4 space-y-2">
                        <span className={`block h-1.5 w-full rounded-full ${isFeatured ? 'bg-white/25' : 'bg-white/10'}`} />
                        <span className={`block h-1.5 w-3/4 rounded-full ${isFeatured ? 'bg-white/20' : 'bg-white/[0.08]'}`} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f7f8fa] py-14 lg:py-20" aria-labelledby="pricing-table-title">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto mb-10 flex max-w-6xl flex-col justify-between gap-3 lg:flex-row lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#1769ff]">{t('pricing.unified.eyebrow')}</p>
              <h2 id="pricing-table-title" className="scroll-mt-24 mt-2 text-3xl font-bold tracking-tight text-slate-950 lg:text-4xl">{t('pricing.compare.title')}</h2>
            </div>
            <p className="max-w-xl text-sm leading-relaxed text-slate-600 lg:text-right">{t('pricing.compare.subtitle')}</p>
          </div>

          <div id="geofleet-pricing" className="mx-auto grid max-w-6xl scroll-mt-24 gap-5 lg:grid-cols-3">
            {PLATFORM_PLANS.map((key) => {
              const featured = key === 'professional';
              const price = PLAN_PRICE_USD[key];
              const perDriverOrders = PLAN_ORDERS_PER_DRIVER[key];
              return (
                <article key={key} className={`relative flex flex-col overflow-hidden rounded-3xl border p-6 sm:p-8 ${featured ? 'border-blue-300 bg-[#f5f8ff] shadow-[0_20px_60px_rgba(23,105,255,0.12)]' : 'border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)]'}`}>
                  {featured ? <span className="absolute end-6 top-6 rounded-full bg-[#1769ff] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">{t('pricing.plans.professional.popular')}</span> : null}
                  <p className="flex items-center gap-2 text-sm font-bold text-blue-700">
                    <i className={`${PLAN_ICONS[key]} text-base`} aria-hidden />
                    {t('pricing.platform.eyebrow')}
                  </p>
                  <h3 className="mt-2 text-2xl font-bold tracking-tight text-slate-950">{t(`pricing.plans.${key}.name`)}</h3>
                  <p className="mt-2 min-h-12 max-w-sm text-sm leading-relaxed text-slate-600">{t(`pricing.plans.${key}.tagline`)}</p>
                  <p className="mt-6 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-5xl font-bold tracking-[-0.045em] text-slate-950">{monthlyFor(key)}</span>
                    {price !== null ? <span className="text-sm font-medium text-slate-500">{t('pricing.perMonth')}</span> : null}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">
                    {price !== null
                      ? t('pricing.platform.details.minimumLine', { amount: formatMoney(price, currency), count: MIN_DRIVERS })
                      : t('pricing.platform.details.quotedLine')}
                  </p>
                  <p className="mt-3 text-sm text-slate-600">{t(`pricing.plans.${key}.description`)}</p>

                  <dl className="mt-5 grid grid-cols-1 gap-2 rounded-2xl border border-slate-200/80 bg-white/70 p-4 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.platform.details.minDrivers')}</dt>
                      <dd className="font-semibold text-slate-900">{price !== null ? MIN_DRIVERS : t('pricing.unified.custom')}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.platform.details.ordersPerDriver')}</dt>
                      <dd className="font-semibold text-slate-900">{perDriverOrders !== null ? perDriverOrders.toLocaleString('en-US') : t('pricing.unified.contracted')}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.platform.details.ordersAtMinimum')}</dt>
                      <dd className="font-semibold text-slate-900">{perDriverOrders !== null ? (perDriverOrders * MIN_DRIVERS).toLocaleString('en-US') : t('pricing.unified.contracted')}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.platform.details.hubs')}</dt>
                      <dd className="font-semibold text-slate-900">{t(key === 'enterprise' ? 'pricing.platform.details.multiRegion' : 'pricing.platform.details.multiple')}</dd>
                    </div>
                  </dl>

                  <ul className="m-0 mt-6 flex-1 list-none space-y-3 border-t border-slate-200 pt-6">
                    {featuresFor(key).map((feature) => (
                      <li key={feature} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700">
                        <i className="ri-check-line mt-0.5 text-base font-bold text-[#1769ff]" aria-hidden />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href={planHref(key)} target="_blank" rel="noopener noreferrer" className={`mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5 ${featured ? 'bg-[#1769ff] text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700' : 'bg-slate-950 text-white hover:bg-slate-800'}`}>
                    {t(`pricing.plans.${key}.cta`)}<i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
                  </a>
                </article>
              );
            })}
          </div>

          <div className="mx-auto mt-6 grid max-w-6xl gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 md:grid-cols-3">
            <p className="flex items-start gap-2.5"><i className="ri-map-pin-2-line mt-0.5 text-[#1769ff]" aria-hidden /><span><strong className="text-slate-900">Built for multiple hubs.</strong> Add locations while keeping one live view of your network.</span></p>
            <p className="flex items-start gap-2.5"><i className="ri-file-list-3-line mt-0.5 text-[#1769ff]" aria-hidden /><span><strong className="text-slate-900">Order allowances are clear.</strong> Each driver includes a defined monthly order volume.</span></p>
            <p className="flex items-start gap-2.5"><i className="ri-message-3-line mt-0.5 text-[#1769ff]" aria-hidden /><span>SMS, WhatsApp and third-party carrier charges are billed separately.</span></p>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="mb-10 text-center text-3xl font-bold tracking-tight text-slate-950">{t('pricing.faqTitle')}</h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => {
              const open = openFaq === index;
              return (
                <div key={faq.question} className={`overflow-hidden rounded-xl border bg-white transition-colors ${open ? 'border-[#1769ff]/30 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}>
                  <button onClick={() => setOpenFaq(open ? null : index)} aria-expanded={open} className="flex w-full cursor-pointer items-center justify-between gap-4 p-5 text-start">
                    <span className="text-sm font-semibold text-slate-950">{faq.question}</span>
                    <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full transition-all ${open ? 'rotate-180 bg-blue-50 text-[#1769ff]' : 'bg-slate-100 text-slate-400'}`} aria-hidden><i className="ri-arrow-down-s-line" /></span>
                  </button>
                  {open && <div className="border-t border-slate-100 px-5 py-4"><p className="text-sm leading-relaxed text-slate-600">{faq.answer}</p></div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#071421] py-20 text-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight lg:text-4xl">{t('pricing.ctaTitle')}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">{t('pricing.ctaSubtitle')}</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a href={LITE_SIGNUP_URL} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap rounded-xl bg-[#1769ff] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0f5bd7]">{t('pricing.ctaStart')}</a>
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap rounded-xl border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">{t('pricing.ctaSales')}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
